import type { LucideIcon } from 'lucide-vue-next'
import type { SubscriptionType } from './user'
import type { RenderType } from './prompt'

export interface GenerationData extends Seed {
	prompt: string
	neg_prompt: string
	upscale: boolean
	fastLane: boolean
	watermark: boolean
	private: boolean
	quality: ImageResolution
	format: ImageFormat
	style_id: number
	creativity: ImageCreativity
	modifiers: Modifier[]
	slow_mode: boolean
	tags?: string
	pro: boolean
	less_nsfw: boolean
	automatic_face_restore: boolean
}

export interface Seed {
	seed: number
	seedLock?: boolean
}

export interface ModifierGroup {
	category: string
	modifiers: Modifier[]
}

export interface Modifier {
	id: number
	lib: string
	weight: number
	tags?: string[]
	sensitive: boolean
	render: RenderType
	is_new: boolean
}

export type Modifiers = ModifierGroup[]

export enum ImageResolution {
	Standard = 0,
	High = 1,
	Ultra = 2,
}

export enum ImageFormat {
	Wide = 0,
	Portrait = 1,
	Square = 2,
}

export enum ImageCreativity {
	Creative = 'creative',
	Balanced = 'balanced',
	Precise = 'precise',
}

export enum TaskState {
	Success = 'SUCCESS',
	Pending = 'PENDING',
	Failure = 'FAILURE',
	Processed = 'PROCESSED',
}

export interface Task {
	name: string
	state: TaskState
	time?: string
}

export interface GeneratorTask extends Omit<GenerationData, 'fastLane' | 'seedLock'> {
	task_id: number
	completed: boolean
	blurhash: string | null
	base64: string | null
	image_id: number | null
	image_uuid: string | null
	error: 'invalid_pixels' | 'invalid_tags' | 'txt2img_fail' | 'unexpected_error' | null
	tasks: Task[]
	created_at: string
	total_time: string | null
	upscale_only: boolean
	cost: number
	queued: boolean
	slow_mode: boolean
	paid_with_daily: boolean
	img2img_type?: 'style_convert' | 'inpaint'
	source_img: string | null
}

export interface GeneratorChoice {
	label: string | (() => string)
	cost: number // Ticket cost
	id: number | string // Choice id (eg: ImageResolution.Standard)
	icon?: LucideIcon
	pro: boolean
	plan?: SubscriptionType
}

export interface GeneratorPreset {
	id: number
	lib: string
	value: string
	trad: string
	favorite: boolean
}

export type GeneratorPresets = Record<string, GeneratorPreset[]>

export interface StyleToStyleBody {
	source_img: number
	target_style: number
	seed: number
}

export enum InpaintType {
	Add = 'add',
	Tweak = 'tweak',
}

export interface InpaintBody {
	prompt: string
	neg_prompt: string
	seed: number
	pro: boolean
	source_img: number
	mask: string
	less_nsfw: boolean
	inpaint_type: InpaintType
}

export interface TweakData {
	prompt: string
	neg_prompt: string
	seed: number
	pro: boolean
	mask: string
	fastLane: boolean
	style_id: number
	less_nsfw: boolean
	inpaint_type: InpaintType
	modifiers: Modifier[]
	automatic_face_restore: boolean
}
